import React, { useState, useEffect, useRef } from "react";
import { Card, Button, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import "./DeliveryAddressSection.scss";
import AddAddressForm from "./AddAddressForm";
import CustomCheckbox from "components/general/CustomCheckbox";
import { Address } from "interfaces/IAddresses";

interface DeliveryAddressSectionProps {
  addresses: Address[];
  selectedAddressId: string | null;
  onAddressSelect: (addressId: string) => void;
  onAddressConfirm: () => void;
  onReturnToAddressSection: () => void;
  isActive: boolean;
  onReloadAddresses?: () => void;
}

const DeliveryAddressSection: React.FC<DeliveryAddressSectionProps> = ({
  addresses,
  selectedAddressId,
  onAddressSelect,
  onAddressConfirm,
  onReturnToAddressSection,
  isActive,
  onReloadAddresses,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddingAddress, setIsAddingAddress] = useState(false);

  // Ordenar direcciones: la predeterminada primero, el resto en su orden original
  const sortedAddresses = [...addresses].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return 0;
  });

  const selectedAddress = addresses.find(
    (addr) => String(addr.id) === selectedAddressId
  );

  const handleCheckboxChange = (itemKey: string, addressId: number) => {
    onAddressSelect(String(addressId));
  };

  const handleAddressAdded = (newAddress: Address) => {
    setIsAddingAddress(false);
    // Set the new address as selected
    onAddressSelect(String(newAddress.id));
  };

  const handleAddressCancelled = () => {
    setIsAddingAddress(false);
  };

  const handleAddAddressClick = () => {
    setIsAddingAddress(true);
    // Scroll hacia el formulario cuando se abre
    setTimeout(() => {
      const formElement = document.querySelector('.add-address-form');
      if (formElement) {
        formElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100); // Pequeño delay para asegurar que el formulario se haya renderizado
  };

  // Function to reload addresses from parent component
  const handleReloadAddresses = () => {
    // Call the parent component's reload function if provided
    if (onReloadAddresses) {
      onReloadAddresses();
    }
  };

  // Si no está activa, mostrar el resumen
  if (!isActive) {
    return (
      <div
        className="delivery-address-section inactive"
        onClick={onReturnToAddressSection}
      >
        <div className="section-header">
          <h3>Dirección de entrega</h3>
          {selectedAddress && (
            <div className="selected-address-summary">
              <div className="recipient-label">Enviar a:</div>
              <div className="address-details">
                {selectedAddress.address}, {selectedAddress.external_number},{" "}
                {selectedAddress.state}, {selectedAddress.city},{" "}
                {selectedAddress.postal_code}
              </div>
              <div className="delivery-person-details">
                Persona de entrega: {selectedAddress.delivery_person}, Teléfono:{" "}
                {selectedAddress.phone}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Si está activa, mostrar el contenido completo
  return (
    <div className="delivery-address-section active">
      <div className="section-header">
        <h3>Dirección de entrega</h3>
        <span>Por favor, selecciona una dirección de entrega</span>
      </div>

      <div className="delivery-address-content">
        {isAddingAddress ? (
          <div className="add-address-container">
            <AddAddressForm
              onAddressAdded={handleAddressAdded}
              onCancel={handleAddressCancelled}
              onReloadAddresses={handleReloadAddresses}
            />
          </div>
        ) : addresses.length === 0 ? (
          <div className="no-addresses-container">
            <p>No tienes direcciones guardadas</p>
            <Button
              variant="primary"
              className="add-address-btn"
              onClick={handleAddAddressClick}
            >
              Añadir dirección
            </Button>
          </div>
        ) : (
          <>
            <div className="addresses-list">
              {sortedAddresses.map((address: Address) => {
                const itemKey = `${address.id}-${address.postal_code}-${address.external_number}`;
                // Verificar si esta dirección está seleccionada (comparando como strings)
                const isSelected = String(address.id) === selectedAddressId;

                return (
                  <div
                    key={address.id}
                    className={`address-item ${
                      isSelected ? "selected-address" : ""
                    }`}
                  >
                    <div className="item-checkbox">
                      <CustomCheckbox
                        checked={isSelected}
                        onChange={() =>
                          handleCheckboxChange(itemKey, address.id)
                        }
                      />
                    </div>
                    <div
                      className="address-details"
                      onClick={() => handleCheckboxChange(itemKey, address.id)}
                    >
                      <p className="address-name">
                        {address.is_default
                          ? "Dirección predeterminada"
                          : "Dirección alternativa"}
                      </p>
                      <p className="address-line">
                        {address.address}, #{address.external_number} ,{" "}
                        {address.neighborhood}, {address.city}, {address.state}{" "}
                        , CP: {address.postal_code}
                      </p>
                      <p className="delivery-person-line">
                        Enviar a: {address.delivery_person}, No. Telefónico:{" "}
                        {address.phone}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="add-address-container">
              <button
                className="circular-add-button"
                onClick={handleAddAddressClick}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
              <span className="add-address-text">Añadir nueva dirección</span>
            </div>

            <div className="address-actions">
              <Button
                variant="primary"
                className="confirm-address-btn"
                disabled={!selectedAddressId}
                onClick={onAddressConfirm}
              >
                Enviar a esta dirección
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeliveryAddressSection;
